import React from "react";
import { PrismAsync as PrismSyntaxHighlighter } from "react-syntax-highlighter";
import styled, { useTheme } from "styled-components";

import { HexButton } from "../../hex-components/HexButton.js";
import { HexCopyToClipboard } from "../../hex-components/HexCopyToClipboard.js";
import { useUniqueId } from "../../hooks/useUniqueId.js";
import { ShowOnHover, ShowOnHoverContainer } from "../common/ShowOnHover.js";
import { CopyIcon } from "../icons/CustomIcons.js";

export interface SyntaxHighlighterProps {
  allowScroll?: boolean;
  children: string;
  className?: string;
  language?: string;
  isInAppView?: boolean;
  copyable?: boolean;
}
/**
 * Forked from https://github.com/nteract/nteract/blob/a96151b5f2d1ae3b7604f82008d9413e1cc5f4c8/packages/presentational-components/src/syntax-highlighter/index.tsx
 */
export const SyntaxHighlighter: React.ComponentType<SyntaxHighlighterProps> =
  React.memo(function SyntaxHighlighter({
    allowScroll = true,
    children,
    className,
    copyable = false,
    isInAppView = false,
    language = "text",
  }) {
    const theme = useTheme();
    const uuid = useUniqueId();
    const codeblockId = `${uuid}-codeblock`;
    const resolvedLanguage = language.startsWith("text/x-")
      ? language.slice("text/x-".length)
      : language;

    return (
      <ShowOnHoverContainer className={className} id={codeblockId}>
        {copyable && (
          <StyledShowOnHover data-controller={codeblockId}>
            <HexCopyToClipboard text={children}>
              <HexButton minimal={true}>
                <CopyIcon />
              </HexButton>
            </HexCopyToClipboard>
          </StyledShowOnHover>
        )}
        <StyledPrismSyntaxHighlighter
          language={resolvedLanguage}
          style={theme.markdownCodeTheme(isInAppView, allowScroll)}
        >
          {children}
        </StyledPrismSyntaxHighlighter>
      </ShowOnHoverContainer>
    );
  });

const StyledPrismSyntaxHighlighter = styled(PrismSyntaxHighlighter)`
  width: 100%;
  height: 100%;
`;

const StyledShowOnHover = styled(ShowOnHover)`
  background-color: ${({ theme }) => theme.backgroundColor.MUTED};
  position: absolute;
  top: 10px;
  right: 11px;
  z-index: 1;
`;
